import {getTestProps} from '@benchling/js-test-utils';
import {Anchor, createStyles, getStylesRef, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import staticURL from 'lib/staticURL';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ONBOARDING_INTRO_TITLE} from './onboardingCopy';
import {OnboardingHeading} from './OnboardingHeading';

const TEST_COMPONENT = 'OnboardingBanner';
const JELLYFISH_GIF_PATH = '/static/img/ai-chat-onboarding/onboarding-jellyfish.gif';

/** How far the jellyfish overflows above the card's top edge, in pixels. */
const JELLYFISH_OVERFLOW_PX = 46;
const JELLYFISH_HEIGHT_PX = 180;
/** Fixed height of the "Start personalizing" card, in pixels. */
const CARD_HEIGHT_PX = 144;
/** Inner padding of the "Start personalizing" card, in pixels. */
const CARD_PADDING_PX = 30;
/** Banner width when the sidepanel is semi-expanded (448px content area). */
const CARD_WIDTH_SEMI_EXPANDED_PX = 340;
/** Banner width when the sidepanel is fully expanded. */
const CARD_WIDTH_EXPANDED_PX = 440;
/** Space between the onboarding banner and the chat input below, in pixels. */
const BANNER_BOTTOM_MARGIN_PX = 30;

const useStyles = createStyles((theme, {isExpanded}: {isExpanded: boolean}) => ({
  root: {
    marginBottom: BANNER_BOTTOM_MARGIN_PX,
    alignItems: 'center',
    width: '100%',
    WebkitFontSmoothing: 'antialiased',
  },
  cardSection: {
    // Leaves room above the card for the jellyfish to overflow without clipping the heading.
    marginTop: JELLYFISH_OVERFLOW_PX,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  // The card is itself the button: "Start personalizing" on the left, the jellyfish centered (and
  // overflowing the top edge), and an arrow on the right that nudges right on hover. The label and
  // arrow sit at the bottom of the card (the jellyfish occupies the space above them).
  card: {
    position: 'relative',
    overflow: 'visible',
    width: isExpanded ? CARD_WIDTH_EXPANDED_PX : CARD_WIDTH_SEMI_EXPANDED_PX,
    height: CARD_HEIGHT_PX,
    padding: CARD_PADDING_PX,
    borderRadius: theme.radius.xl,
    backgroundColor: theme.colors.blue[0],
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    transition: 'width 0.3s ease-in-out, background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
    [`&:hover .${getStylesRef('arrow')}`]: {
      transform: 'translateX(4px)',
    },
  },
  startText: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    lineHeight: 1.2,
    color: theme.colors.blue[7],
  },
  jellyfish: {
    position: 'absolute',
    left: '50%',
    top: -JELLYFISH_OVERFLOW_PX,
    transform: 'translateX(-50%)',
    height: JELLYFISH_HEIGHT_PX,
    width: 'auto',
    pointerEvents: 'none',
  },
  arrow: {
    ref: getStylesRef('arrow'),
    color: theme.colors.blue[7],
    fontSize: theme.fontSizes.xl,
    fontWeight: 500,
    transition: 'transform 150ms ease',
  },
}));

/**
 * The inline onboarding invite shown at the top of the welcome panel for users who haven't yet
 * onboarded. The whole lavender card (with its jellyfish illustration) is the "Start personalizing"
 * affordance that launches the onboarding survey; a quieter "Maybe later" link under the heading
 * dismisses it. Because
 * it lives inside the welcome panel, onboarding stays optional — the user can ignore it and type a
 * chat directly.
 */
interface OnboardingBannerProps {
  isExpanded: boolean;
  onStart: () => void;
  onSkip: () => void;
}

export function OnboardingBanner({isExpanded, onStart, onSkip}: OnboardingBannerProps): JSX.Element {
  const {classes} = useStyles({isExpanded});

  return (
    <Stack
      spacing="md"
      className={classes.root}
      {...getTestProps({component: TEST_COMPONENT, element: 'root'})}
    >
      <OnboardingHeading
        title={ONBOARDING_INTRO_TITLE}
        action={
          <Anchor
            component="button"
            type="button"
            size="lg"
            color="blue"
            onClick={onSkip}
            {...getTestProps({component: TEST_COMPONENT, element: 'skip-button'})}
          >
            Maybe later
          </Anchor>
        }
      />
      <Stack spacing="sm" className={classes.cardSection} align="center">
        <UnstyledButton
          aria-label="Start personalizing"
          className={classes.card}
          onClick={onStart}
          {...getTestProps({component: TEST_COMPONENT, element: 'start-button'})}
        >
          <Text className={classes.startText}>
            Start
            <br />
            personalizing
          </Text>
          <img
            src={staticURL(JELLYFISH_GIF_PATH)}
            alt=""
            className={classes.jellyfish}
            {...getTestProps({component: TEST_COMPONENT, element: 'illustration'})}
          />
          <BenchlingIcon icon="arrow-right-long" aria-hidden={true} className={classes.arrow} />
        </UnstyledButton>
      </Stack>
    </Stack>
  );
}
